/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { StackSpacing, QueueSpacing } from "@components/spacing";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { InputText, InputAutoComplete } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { ACCOUNT, EBINQUIRY } from "app/shared/services/services-model.type";
import {
  getBillingInfoPendingSelector,
  getBillingMakeFeeSelector,
  getBillingPackagePendingSelector,
  getBillingPackageSelector,
  getBillingResCustomerInfoSelector,
  getBillingStoreSelector,
} from "../../reducer/selectors";
import { useDispatch, useSelector } from "react-redux";
import { DataBillingCreate } from "../../reducer/types";
import { ServiceCode } from "app/shared/services/api-path";
import { fetchBillingPackageRequest, setBillingStore } from "../../reducer/actions";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import BeneModal from "app/shared/components/modal/bene-account-source/select-bene.modal";
import { Constant } from "app/shared/constant/constant";
import { PROFILE_BENE } from "../../../beneficiaries/utils/bene.utils";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import {
  getBillingPackage,
  getBillingProvider,
  getBillingService,
  handlerBillingPackages,
  handlerBillingProviders,
  handlerBillingServices,
  loadBillingServiceJson,
} from "../../billing-utils/billing-utils";

export interface FormBillingProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
  requestMakeFee: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  serviceType,
  serviceName,
  providerID,
  providerName,
  packageID,
  packageName,
  customerCode,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormBillingData = {
  serviceType: string;
  serviceName: string;
  providerID: string;
  providerName: string;
  packageID: string;
  packageName: string;
  customerCode: string;
};

const FormBilling = (props: FormBillingProps) => {
  const { onSubmit, requestCreate, onCancel, accountDebit, requestMakeFee, showModal, closeModal } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();
  const dispatch = useDispatch();

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, trigger, clearErrors } = useForm<FormBillingData>({
    defaultValues: {
      serviceType: "",
      serviceName: "",
      providerID: "",
      providerName: "",
      packageID: "",
      packageName: "",
      customerCode: "",
    },
    mode: "onChange",
  });

  const billingStore = useSelector(getBillingStoreSelector);
  const responsePackage = useSelector(getBillingPackageSelector);
  const responseMakeFee = useSelector(getBillingMakeFeeSelector);
  const pendingPackage = useSelector(getBillingPackagePendingSelector);
  const pendingInfo = useSelector(getBillingInfoPendingSelector);
  const customerInfo = useSelector(getBillingResCustomerInfoSelector);

  const [serviceArray, setServiceArray] = React.useState<ItemCompleteProps[]>([]);
  const [providerArray, setProviderArray] = React.useState<ItemCompleteProps[]>([]);
  const [packageArray, setPackageArray] = React.useState<ItemCompleteProps[]>([]);
  const [dataFee, setDataFee] = React.useState<EBINQUIRY | undefined>(undefined);
  const [isBene, setIsBene] = React.useState<boolean>(false);
  const [accountBene, setAccountBene] = React.useState<PROFILE_BENE | undefined>();

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors.text, colors.notification],
  );

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    serviceType: {
      required: {
        value: true,
        message: t("BILLING_VUI_LONG_CHON_DICH_VU"),
      },
    },
    serviceName: {
      required: {
        value: false,
        message: t("BILLING_VUI_LONG_CHON_DICH_VU"),
      },
    },
    providerID: {
      required: {
        value: true,
        message: t("BILLING_VUI_LONG_CHON_NHA_CUNG_CAP"),
      },
    },
    providerName: {
      required: {
        value: false,
        message: t("BILLING_VUI_LONG_CHON_NHA_CUNG_CAP"),
      },
    },
    packageID: {
      required: {
        value: packageArray.length ? true : false,
        message: t("BILLING_VUI_LONG_CHON_GOI_DICH_VU"),
      },
    },
    packageName: {
      required: {
        value: false,
        message: t("BILLING_VUI_LONG_CHON_GOI_DICH_VU"),
      },
    },
    customerCode: {
      required: {
        value: true,
        message: t("BILLING_VUI_LONG_NHAP_MA_HOA_DON"),
      },
    },
  };

  const handleRequestPackage = React.useCallback(
    async (serviceType?: string, providerID?: string) => {
      if (serviceType && providerID) {
        const dataUpdate: DataBillingCreate = {
          SERVICE: {
            value: serviceType,
            label: "",
          },
          PROVIDER: {
            value: providerID,
            label: "",
          },
          SERVICE_CODE: ServiceCode.PAYMENT_BILLING,
        };
        dispatch(fetchBillingPackageRequest(dataUpdate));
      }
    },
    [dispatch],
  );

  const handleRequestCreate = (value: FormBillingData): void => {
    const dataUpdate: DataBillingCreate = {
      ACCOUNT_DEBIT: accountDebit,
      SERVICE: { value: value.serviceType, label: value.serviceName },
      PROVIDER: { value: value.providerID, label: value.providerName },
      SERVICE_CODE: ServiceCode.PAYMENT_BILLING,
      INQUIRY_CODE: value.customerCode,
      BENE: isBene && accountBene?.CUSTOMER_CODE?.length && accountBene?.CUSTOMER_CODE === value.customerCode ? accountBene : undefined,
    };
    if (packageArray.length && value.packageID.length) {
      dataUpdate.PACKAGE = { value: value.packageID, label: value.packageName };
    }
    requestCreate(dataUpdate);
  };

  const handleRequestMakeFee = (value: FormBillingData): void => {
    const dataUpdate: DataBillingCreate = {
      ACCOUNT_DEBIT: accountDebit,
      SERVICE: { value: value.serviceType, label: value.serviceName },
      PROVIDER: { value: value.providerID, label: value.providerName },
      SERVICE_CODE: ServiceCode.PAYMENT_BILLING,
      INQUIRY_CODE: value.customerCode,
    };
    if (packageArray.length && value.packageID.length) {
      dataUpdate.PACKAGE = { value: value.packageID, label: value.packageName };
    }
    if (dataFee) setDataFee(undefined);
    requestMakeFee(dataUpdate);
  };

  const handlerSelectBene = (temp: PROFILE_BENE) => {
    closeModal();
    setIsBene(true);
    setAccountBene(temp);
    setValue("serviceType", temp.SERVICE || "");
    clearErrors("serviceType");
    setValue("customerCode", temp.CUSTOMER_CODE || "");
    clearErrors("customerCode");
    setValue("providerID", temp.PROVIDER || "");
    clearErrors("providerID");
    setValue("packageID", temp.SERVICE_PACKAGE || "");
    clearErrors("packageID");
    if (providerArray.length) setProviderArray([]);
    if (packageArray.length) setPackageArray([]);
    const listProvider = handlerBillingProviders(temp.SERVICE);
    setTimeout(() => {
      setProviderArray(listProvider);
      if (temp.SERVICE?.length && temp.SERVICE_PACKAGE?.length) {
        handleRequestPackage(temp.SERVICE, temp.PROVIDER);
      }
    }, 0);
  };

  /// MARK: - Action Handler
  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressFeeDetail = async () => {
    onSubmit();
    handleSubmit(handleRequestMakeFee).call(() => {});
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(
      <BeneModal
        title={t("BENE_CHON_HOA_DON_DANH_SACH")}
        templateType={Constant.TEMPLATE_TYPE.BILLING_TEMPLATE}
        onPressAcceptBene={handlerSelectBene}
      />,
    );
  };

  const onClickProvider = async () => {
    trigger("serviceType");
  };

  const checkSelectProvider = () => {
    if (getValues("serviceType").length) return false;
    return true;
  };

  ///

  /// MARK: - Bussiness Logic HERE
  const onSelectedService = (service?: ItemCompleteProps) => {
    if (accountBene?.SERVICE && service?.value !== accountBene?.SERVICE) setAccountBene(undefined);
    if (providerArray) setProviderArray([]);
    if (packageArray) setPackageArray([]);
    console.log("onSelectedService: ", service);
    setValue("serviceName", service?.label || "");
    clearErrors("customerCode");
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
    if (getValues("providerID").length) {
      setValue("providerID", "");
      setValue("providerName", "");
      clearErrors("providerID");
      if (getValues("packageID").length) {
        setValue("packageID", "");
        setValue("packageName", "");
        clearErrors("packageID");
      }
    }
    if (customerInfo?.INQUIRY_CODE?.length) {
      dispatch(setBillingStore({ ...billingStore, customerInfo: undefined }));
    }
    setTimeout(() => {
      const listProvider = handlerBillingProviders(service?.value);
      setProviderArray(listProvider);
    }, 400);
  };

  const onSelectedProvider = (provider?: ItemCompleteProps) => {
    setValue("providerName", provider?.label || "");
    if (accountBene?.PROVIDER?.length && accountBene?.PROVIDER !== provider?.value) setAccountBene(undefined);
    if (packageArray) setPackageArray([]);
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
    if (getValues("packageID").length) {
      setValue("packageID", "");
      setValue("packageName", "");
      clearErrors("packageID");
    }
    const serviceType = getValues("serviceType");
    if (serviceType.length && provider?.value.length) {
      handleRequestPackage(serviceType, provider.value);
    }
  };

  const onSelectedPackage = (packageItem?: ItemCompleteProps) => {
    if (accountBene?.SERVICE_PACKAGE?.length && accountBene?.SERVICE_PACKAGE !== packageItem?.value) setAccountBene(undefined);
    setValue("packageName", packageItem?.label || "");
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
  };

  // set thông tin billing
  const onBlurTopUpCustomer = async () => {
    trigger("customerCode");
    const customerCode = getValues("customerCode") || "";
    if (customerCode.length && accountBene?.CUSTOMER_CODE === customerCode) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
  };

  ///
  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );
  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputAutoComplete
        rules={validateSchemeForm.serviceType}
        name="serviceType"
        options={serviceArray}
        pending={pendingInfo}
        control={control}
        title={t("TOPUP_DICH_VU")}
        label={t("TOPUP_CHON_DICH_VU")}
        onDismiss={onSelectedService}
        showSearch={true}
        showIcon={true}
      />
      <InputAutoComplete
        rules={validateSchemeForm.providerID}
        name="providerID"
        options={providerArray}
        pending={pendingInfo}
        control={control}
        title={t("TOPUP_NHA_CUNG_CAP")}
        label={t("TOPUP_CHON_NHA_CUNG_CAP")}
        onClick={onClickProvider}
        checkSelect={checkSelectProvider()}
        onDismiss={onSelectedProvider}
        showSearch={true}
      />
      <InputAutoComplete
        rules={validateSchemeForm.packageID}
        name="packageID"
        options={packageArray}
        pending={pendingPackage}
        control={control}
        title={t("BILLING_GOI_DICH_VU")}
        label={t("BILLING_GOI_DICH_VU")}
        onDismiss={onSelectedPackage}
        showSearch={true}
        isShow={packageArray.length && getValues("providerID").length ? true : false}
      />
      <InputText
        name="customerCode"
        control={control}
        rules={validateSchemeForm.customerCode}
        inputProps={{ right: chooseAccountBene }}
        label={t("BILLING_MA_KHACH_HANG")}
        note={t("BILLING_MA_HOA_DON_KHACH_HANG")}
        onBlur={onBlurTopUpCustomer}
      />
      <FormViewFee onClick={onPressFeeDetail} />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  ///
  ///
  React.useEffect(() => {
    loadBillingServiceJson().then(() => {
      const listService = handlerBillingServices();
      setServiceArray(listService);
    });
  }, []);

  React.useEffect(() => {
    let listPackage: ItemCompleteProps[] = [];
    if (responsePackage) {
      listPackage = handlerBillingPackages(responsePackage);
    }
    if (getValues("serviceType").length) {
      setPackageArray(listPackage);
    }
  }, [getValues, responsePackage]);

  React.useEffect(() => {
    if (accountBene?.SERVICE?.length && accountBene?.SERVICE === getValues("serviceType")) {
      const serviceName = getBillingService(accountBene?.SERVICE)?.NAME || "";
      setValue("serviceName", serviceName);
    }
    if (
      accountBene?.SERVICE?.length &&
      accountBene?.SERVICE === getValues("serviceType") &&
      accountBene?.PROVIDER?.length &&
      providerArray
    ) {
      setValue("providerID", accountBene?.PROVIDER);
      const providerName = getBillingProvider(accountBene?.SERVICE, accountBene?.PROVIDER)?.NAME || "";
      setValue("providerName", providerName);
    }
  }, [accountBene, getValues, providerArray, setValue]);

  React.useEffect(() => {
    if (
      accountBene?.SERVICE?.length &&
      accountBene?.SERVICE === getValues("serviceType") &&
      accountBene?.SERVICE_PACKAGE &&
      packageArray.length
    ) {
      setValue("packageID", accountBene.SERVICE_PACKAGE);
      const packageName = getBillingPackage(responsePackage, accountBene.SERVICE_PACKAGE)?.NAME || "";
      setValue("packageName", packageName);
    }
  }, [accountBene, getValues, packageArray, responsePackage, setValue]);

  React.useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // show Phí
  React.useEffect(() => {
    if (dataFee) {
      showModal(<FeeDetailModalNew data={dataFee} feeType={Constant.TRANSFER.TRANSFER_FEE_TYPE.O} />);
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormBilling;
